<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="login-page">
        <q-banner
          class="announcement-banner bg-amber-100 text-amber-900"
          rounded
          dense
        >
          <template v-slot:avatar>
            <q-icon name="info" color="amber" />
          </template>
          {{ $t('systemNotice') }}
        </q-banner>
        
        <q-card class="login-container">
          
          <q-card-section class="login-left" v-if="!isMobile">
            <div class="image-container">
              <img :src="loginImage" alt="SchoolPeak" class="login-image" />
            </div>
            <div class="login-info">
              <h2 class="title">SchoolPeak</h2>
              <p class="subtitle">{{ $t("findActivity") }}</p>
            </div>
          </q-card-section>
          
          <q-card-section class="login-right">
            <div class="header-container">
              <div class="language-switcher">
                <q-btn-dropdown flat color="grey-7" :label="getLanguageFullName(currentLang)" class="grey-dropdown">
                  <q-list>
                    <q-item v-for="lang in languages" :key="lang" clickable v-close-popup @click="changeLanguage(lang)">
                      <q-item-section>{{ getLanguageFullName(lang) }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </div>
              
              <div v-if="tenantLogo" class="tenant-logo-container">
                <img :src="tenantLogo" alt="School Logo" class="tenant-logo" />
              </div>
            </div>
            
            <div class="form-header">
              <h1 class="welcome-text">{{ $t("welcome") }}</h1>
              <p class="login-subtext">{{ $t("enterCredentials") || 'Please enter your credentials to log in' }}</p>
            </div>
            
            <q-form @submit.prevent="login" class="login-form">
              <q-input
                v-model="email"
                type="email"
                :placeholder="$t('userNameEmail')"
                outlined
                class="q-mb-md"
              >
                <template v-slot:prepend>
                  <q-icon name="email" />
                </template>
              </q-input>
              
              <q-input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('password')"
                outlined
                class="q-mb-lg"
              >
                <template v-slot:prepend>
                  <q-icon name="lock" />
                </template>
                <template v-slot:append>
                  <q-icon
                    :name="showPassword ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </q-input>
              
              <q-btn
                type="submit"
                color="primary"
                :label="$t('signIn')"
                class="full-width sign-in-btn"
                unelevated
                rounded
                :loading="isLoggingIn"
              />
              
              <div class="forgot-password-container">
                <q-btn
                  flat
                  color="secondary"
                  :label="$t('forgotPassword')"
                  @click="openResetPasswordModal"
                  class="forgot-btn"
                  no-caps
                />
              </div>
            </q-form>
            
            <q-banner
              v-if="errorMessage"
              class="error-message bg-negative text-white"
              rounded
              dense
            >
              <template v-slot:avatar>
                <q-icon name="error" />
              </template>
              {{ errorMessage }}
            </q-banner>
          </q-card-section>
        </q-card>
        
        <!-- Role Selection Modal -->
        <role-selection-modal
          v-model:visible="showRoleSelectionModal"
          :available-roles="availableRoles"
          @role-selected="handleRoleSelection"
        />
        
        <reset-password-modal v-model:visible="showResetPasswordModal" />
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import {
  QLayout,
  QPage,
  QPageContainer,
  QCard,
  QCardSection,
  QBtn,
  QBtnDropdown,
  QList,
  QItem,
  QItemSection,
  QForm,
  QInput,
  QBanner,
  QIcon,
} from 'quasar';
import AuthService from '../services/authService';
import TenantService from '../services/tenantService';
import ResetPasswordModal from '../components/modals/ResetPasswordModal.vue';
import RoleSelectionModal from '../components/modals/RoleSelectionModal.vue';
import loginImage from '@/assets/login.jpeg';
import { setI18nLanguage } from '../i18n';
import API from '../services/apiService';

export default {
  components: {
    QLayout,
    QPage,
    QPageContainer,
    QCard,
    QCardSection,
    QBtn,
    QBtnDropdown,
    QList,
    QItem,
    QItemSection,
    QForm,
    QInput,
    QBanner,
    QIcon,
    ResetPasswordModal,
    RoleSelectionModal,
  },
  data() {
    return {
      email: '',
      password: '',
      errorMessage: '',
      languages: ['en', 'de', 'lv', 'ru'],
      currentLang: localStorage.getItem('user-language') || this.$i18n.locale,
      isAdmin: false,
      loginImage,
      isMobile: false,
      showResetPasswordModal: false,
      showRoleSelectionModal: false,
      availableRoles: [],
      tenantLogo: null,
      tenantName: null,
      showPassword: false,
      isLoggingIn: false,
      loginResponse: null // Store the login response when multiple roles exist
    };
  },
  async created() {
    await this.fetchTenantInfo();
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    async fetchTenantInfo() {
      try {
        const tenantInfo = await TenantService.getTenantInfo();
        this.tenantName = tenantInfo.name;
        this.tenantLogo = tenantInfo.logoUrl;
      } catch (error) {
        console.error('Error fetching tenant info:', error);
        this.tenantLogo = null;
      }
    },
    async login() {
      this.errorMessage = '';
      if (!this.email || !this.password) {
        this.errorMessage = this.$t('Email and password are required');
        return;
      }
      
      try {
        this.isLoggingIn = true;
        const response = await AuthService.login(this.email, this.password);
        
        // Check if user has multiple unique role categories (admin, teacher, parent, child)
        const uniqueRoleCategories = [...new Set(response.roles)];
        
        if (uniqueRoleCategories.length > 1) {
          // Format roles for the selection modal - group by main role category
          this.availableRoles = uniqueRoleCategories.map(roleName => {
            // Find the first roleId that corresponds to this role category
            const roleIndex = response.roles.findIndex(r => r === roleName);
            const roleId = roleIndex >= 0 ? response.roleIds[roleIndex] : null;
            
            return {
              name: roleName,
              id: roleId
            };
          });
          
          this.loginResponse = response; // Store the response for later use
          this.showRoleSelectionModal = true;
        } else {
          // User has only one role category, proceed with normal login flow
          this.redirectUser(response.roles, response.roleIds[0]);
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || 'An error occurred during login';
      } finally {
        this.isLoggingIn = false;
      }
    },
    handleRoleSelection(selectedRole) {
      // Set the active role and redirect accordingly
      const selectedRoleId = selectedRole.id;
      const selectedRoleName = selectedRole.name;
      
      // Set the selected role as the primary role
      localStorage.setItem('role-id', selectedRoleId);
      localStorage.setItem('active-role', selectedRoleName);
      
      // Redirect based on the selected role
      this.redirectUser([selectedRoleName], selectedRoleId);
    },
    async redirectUser(roles) {
      const routes = {
        parent: 'ParentsHome',
        child: 'ChildOverview',
        admin: 'AdminDashboard',
        teacher: 'TeacherOverview',
      };
      const redirectPath = localStorage.getItem('redirectPath') || '/'
      localStorage.removeItem('redirectPath')
      
      if (redirectPath !== '/') {
        this.$router.push(redirectPath);
        return;
      }
      
      // Find the active role from the provided roles array
      let activeRole = roles[0]; // Default to first role
      if (roles.length > 1 && localStorage.getItem('active-role')) {
        // Use the selected role if available
        activeRole = localStorage.getItem('active-role');
      }

      // For teachers, check if they have a homepage preference
      if (activeRole === 'teacher') {
        try {
          const response = await API.get('/users/preferences/homepage');
          if (response.data?.type === 'cohort' && response.data?.targetId) {
            // Redirect to the preferred cohort
            this.$router.push({ 
              name: 'TeacherCohortDetail', 
              params: { cohortId: response.data.targetId }
            });
            return;
          }
        } catch (error) {
          console.error('Error checking homepage preference:', error);
          // Fall through to default route if there's an error
        }
      }

      if (routes[activeRole]) {
        this.$router.push({ name: routes[activeRole] });
      } else {
        this.errorMessage = 'User role is not recognized or missing';
      }
    },
    changeLanguage(lang) {
      setI18nLanguage(lang);
      this.currentLang = lang;
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    openResetPasswordModal() {
      this.showResetPasswordModal = true;
    },
    getLanguageFullName(code) {
      const languages = {
        'en': 'English',
        'de': 'Deutsch',
        'lv': 'Latviešu',
        'ru': 'Русский'
      };
      return languages[code] || code.toUpperCase();
    }
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
}

.login-container {
  display: flex;
  flex-direction: row;
  max-width: 1000px;
  width: 90%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
}

.login-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background-color: var(--q-primary);
  background-image: linear-gradient(135deg, var(--q-primary) 0%, color-mix(in srgb, var(--q-primary), white 15%) 100%);
  color: white;
  width: 45%;
}

.image-container {
  position: relative;
  width: 100%;
  margin-bottom: 30px;
}

.login-image {
  width: 100%;
  max-width: 450px;
  height: auto;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.tenant-logo-container {
  display: flex;
  align-items: center;
}

.tenant-logo {
  width: 90px;
  height: 90px;
  object-fit: contain;
  border-radius: 8px;
  background-color: white;
  padding: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.language-switcher {
  display: flex;
  justify-content: flex-end;
}

.grey-dropdown {
  color: #616161;
  font-size: 14px;
  opacity: 0.85;
}

.login-info {
  text-align: center;
  margin-top: 20px;
  color: white;
}

.title {
  font-size: 30px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  margin-bottom: 10px;
}

.subtitle {
  font-size: 18px;
  font-weight: 400;
  opacity: 0.9;
  margin: 0;
}

.login-right {
  flex: 1;
  padding: 50px;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.form-header {
  margin-bottom: 40px;
}

.welcome-text {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  color: #333;
  margin-bottom: 10px;
}

.login-subtext {
  color: #777;
  font-size: 16px;
  margin: 0;
}

.login-form {
  margin-bottom: 20px;
}

.sign-in-btn {
  margin-top: 10px;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.forgot-password-container {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}

.forgot-btn {
  font-size: 14px;
}

.error-message {
  margin-top: 20px;
}

.announcement-banner {
  margin-bottom: 20px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  border-left: 4px solid #f59e0b;
  max-width: 1000px;
  width: 90%;
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    width: 100%;
    max-width: 450px;
  }
  
  .login-left {
    width: 100%;
    padding: 30px 20px;
  }
  
  .login-right {
    padding: 30px 20px;
  }
  
  .header-container {
    flex-direction: column;
    align-items: flex-end;
    gap: 15px;
  }
  
  .tenant-logo-container {
    align-self: flex-end;
  }
  
  .tenant-logo {
    width: 60px;
    height: 60px;
  }
  
  .welcome-text {
    font-size: 24px;
  }
  
  .form-header {
    margin-bottom: 25px;
  }
  
  .announcement-banner {
    margin-bottom: 15px;
    padding: 10px 14px;
  }
}
</style>